import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, X } from 'lucide-react';
import { typography } from '../../styles/typography';
import CreateMultipleQR from './CreateMultipleQR';
import { locationQrApi } from './locationQrApi';
import { fetchSiteAreasForQr, fetchSitesForQr } from './qrFacilityData';
import HaltPicker from '../ScheduleARide/Halt_Picker';
import { QR_CREATE_PANEL_CLASS } from './qrFormData';

const INPUT_CLASS =
  'w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-base text-black font-["Open_Sans"] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6BABA0]/40 focus:border-[#6BABA0]';

const LABEL_CLASS = 'block text-sm font-medium text-black mb-2 font-["Open_Sans"]';

const Field = ({ label, children, hint }) => (
  <div>
    <label className={LABEL_CLASS}>{label}</label>
    {children}
    {hint && (
      <p className="mt-1 text-xs text-gray-500 font-['Open_Sans']">{hint}</p>
    )}
  </div>
);

const TABS = [
  { id: 'single', label: 'Create single QR' },
  { id: 'multiple', label: 'Create multiple QR' },
];

const SINGLE_LOCATION_TYPES = [
  { id: 'block_floor', label: 'Block / floor' },
  { id: 'street', label: 'Street' },
  { id: 'amenity', label: 'Amenity' },
];

const emptySingleForm = {
  location_type: 'block_floor',
  site_id: '',
  block_id: '',
  floor_no: '',
  amenity_id: '',
  name: '',
  landmark: '',
  location: null,
};

const CreateQR = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('single');
  const [form, setForm] = useState(emptySingleForm);
  const [sites, setSites] = useState([]);
  const [siteAreas, setSiteAreas] = useState({
    blocks: [],
    amenities: [],
    streets: [],
  });
  const [sitesLoading, setSitesLoading] = useState(true);
  const [areasLoading, setAreasLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [bulkSiteId, setBulkSiteId] = useState('');
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  const activeSiteId = activeTab === 'single' ? form.site_id : bulkSiteId;

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    let cancelled = false;

    const loadSites = async () => {
      setSitesLoading(true);
      setError('');
      try {
        const list = await fetchSitesForQr();
        if (cancelled) return;
        setSites(list);
        const firstId = list[0]?.site_id || '';
        setForm(prev => ({ ...prev, site_id: prev.site_id || firstId }));
        setBulkSiteId(prev => prev || firstId);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load sites');
        }
      } finally {
        if (!cancelled) setSitesLoading(false);
      }
    };

    loadSites();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!activeSiteId) {
      setSiteAreas({ blocks: [], amenities: [], streets: [] });
      return undefined;
    }

    let cancelled = false;

    const loadAreas = async () => {
      setAreasLoading(true);
      try {
        const areas = await fetchSiteAreasForQr(activeSiteId);
        if (!cancelled) {
          setSiteAreas({
            blocks: areas?.blocks ?? [],
            amenities: areas?.amenities ?? [],
            streets: areas?.streets ?? [],
          });
        }
      } catch {
        if (!cancelled) {
          setSiteAreas({ blocks: [], amenities: [], streets: [] });
        }
      } finally {
        if (!cancelled) setAreasLoading(false);
      }
    };

    loadAreas();
    return () => {
      cancelled = true;
    };
  }, [activeSiteId]);

  const { blocks, amenities, streets } = siteAreas;

  const blockOptions =
    form.location_type === 'street' ? streets : blocks;

  const selectedBlock = useMemo(
    () => blockOptions.find(b => b.id === form.block_id),
    [blockOptions, form.block_id]
  );

  const handleSingleSiteChange = siteId => {
    setForm(prev => ({
      ...prev,
      site_id: siteId,
      block_id: '',
      floor_no: '',
      amenity_id: '',
      location: null,
    }));
  };

  const handleLocationTypeChange = locationType => {
    setForm(prev => ({
      ...prev,
      location_type: locationType,
      block_id: '',
      floor_no: '',
      amenity_id: '',
    }));
  };

  const validateSingleForm = () => {
    if (!form.site_id) {
      setError('Site is required');
      return false;
    }

    if (form.location_type === 'amenity') {
      if (!form.amenity_id) {
        setError('Amenity is required');
        return false;
      }
      return true;
    }

    if (!form.block_id) {
      setError(form.location_type === 'street' ? 'Street is required' : 'Block is required');
      return false;
    }

    if (form.floor_no === '' || form.floor_no == null) {
      setError('Floor is required');
      return false;
    }

    return true;
  };

  const buildSinglePayload = () => {
    const payload = {
      site_id: form.site_id,
    };

    if (form.landmark.trim()) {
      payload.landmark = form.landmark.trim();
    }

    if (form.name.trim()) {
      payload.name = form.name.trim();
    }

    if (form.location_type === 'amenity') {
      payload.location_type = 'amenity';
      payload.amenity_id = form.amenity_id;
    } else {
      payload.location_type = 'block_floor';
      payload.block_id = form.block_id;
      payload.floor_no = Number(form.floor_no);
      const floor = selectedBlock?.floors?.find(
        f => f.floor_no === Number(form.floor_no)
      );
      if (floor?.name) {
        payload.floor_name = floor.name;
      }
    }

    if (form.location?.lat != null && form.location?.lng != null) {
      payload.location = {
        latitude: form.location.lat,
        longitude: form.location.lng,
      };
    }

    return payload;
  };

  const handleSingleSubmit = async e => {
    e.preventDefault();
    setError('');
    if (!validateSingleForm()) return;

    setIsSaving(true);
    try {
      await locationQrApi.create(buildSinglePayload());
      navigate('/qr-management');
    } catch (err) {
      setError(err.message || 'Failed to generate QR code');
    } finally {
      setIsSaving(false);
    }
  };

  const handleMultipleGenerate = async ({ site_id, selections, options }) => {
    setError('');
    if (!selections?.length) {
      setError('Select at least one location to generate QR codes');
      return;
    }

    setIsSaving(true);
    try {
      const result = await locationQrApi.bulkGenerate({
        site_id,
        selections,
        options: options || { skip_existing: true },
      });

      const created = result.created ?? 0;
      const skipped = result.skipped ?? 0;
      const failed = result.failed ?? 0;

      if (created === 0 && failed > 0) {
        setError(`No QR codes were created. ${failed} failed.`);
        return;
      }

      const parts = [`${created} created`];
      if (skipped > 0) parts.push(`${skipped} skipped (already exist)`);
      if (failed > 0) parts.push(`${failed} failed`);

      if (created > 0) {
        window.alert(`Bulk generate complete: ${parts.join(', ')}.`);
        navigate('/qr-management');
      } else {
        setError(parts.join(', '));
      }
    } catch (err) {
      setError(err.message || 'Failed to generate QR codes');
    } finally {
      setIsSaving(false);
    }
  };

  const tabButtonClass = tabId =>
    `flex-1 px-4 py-3 rounded-xl border text-sm font-semibold font-['Open_Sans'] transition-all ${
      activeTab === tabId
        ? 'border-green-600 bg-green-600 text-white shadow-md ring-2 ring-green-400/60'
        : 'border-white/40 bg-white/10 backdrop-blur-sm text-gray-700 hover:bg-white/15 hover:border-green-400/50 hover:text-gray-900'
    }`;

  const renderSingleForm = () => (
    <form onSubmit={handleSingleSubmit} className="flex flex-col flex-1 p-5 sm:p-6 gap-5">
      <p className="text-sm font-semibold text-black font-['Open_Sans']">
        Create single location QR
      </p>

      {error && activeTab === 'single' && (
        <p className="text-sm text-red-600 font-['Open_Sans']" role="alert">
          {error}
        </p>
      )}

      <Field label="Location type">
        <div className="flex flex-col sm:flex-row gap-2">
          {SINGLE_LOCATION_TYPES.map(type => (
            <button
              key={type.id}
              type="button"
              onClick={() => handleLocationTypeChange(type.id)}
              disabled={isSaving}
              className={`flex-1 px-3 py-2.5 rounded-lg border text-sm font-semibold font-['Open_Sans'] ${
                form.location_type === type.id
                  ? 'border-green-600 bg-green-600 text-white'
                  : 'border-zinc-300 bg-white text-gray-800 hover:border-[#6BABA0]'
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>
      </Field>

      <Field label="Site">
        <select
          value={form.site_id}
          onChange={e => handleSingleSiteChange(e.target.value)}
          className={INPUT_CLASS}
          disabled={isSaving || sitesLoading}
        >
          <option value="">Select site</option>
          {sites.map(site => (
            <option key={site.site_id} value={site.site_id}>
              {site.site_name}
            </option>
          ))}
        </select>
      </Field>

      {areasLoading ? (
        <div className="flex justify-center py-6">
          <div className="w-7 h-7 border-4 border-[#6BABA0] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : form.location_type === 'amenity' ? (
        <Field label="Amenity">
          <select
            value={form.amenity_id}
            onChange={e => updateField('amenity_id', e.target.value)}
            className={INPUT_CLASS}
            disabled={isSaving}
          >
            <option value="">Select amenity</option>
            {amenities.map(amenity => (
              <option key={amenity.id} value={amenity.id}>
                {amenity.name}
              </option>
            ))}
          </select>
        </Field>
      ) : (
        <>
          <Field label={form.location_type === 'street' ? 'Street' : 'Block'}>
            <select
              value={form.block_id}
              onChange={e => {
                const blockId = e.target.value;
                const block = blockOptions.find(b => b.id === blockId);
                const defaultFloor = block?.floors?.[0]?.floor_no;
                setForm(prev => ({
                  ...prev,
                  block_id: blockId,
                  floor_no:
                    defaultFloor != null && prev.location_type === 'street'
                      ? String(defaultFloor)
                      : '',
                }));
              }}
              className={INPUT_CLASS}
              disabled={isSaving}
            >
              <option value="">
                {form.location_type === 'street' ? 'Select street' : 'Select block'}
              </option>
              {blockOptions.map(item => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Floor"
            hint={
              form.location_type === 'street'
                ? 'Villa streets usually use the ground level (floor 0).'
                : undefined
            }
          >
            <select
              value={form.floor_no}
              onChange={e => updateField('floor_no', e.target.value)}
              className={INPUT_CLASS}
              disabled={isSaving || !form.block_id}
            >
              <option value="">Select floor</option>
              {(selectedBlock?.floors ?? []).map(floor => (
                <option key={floor.floor_no} value={floor.floor_no}>
                  {floor.name}
                </option>
              ))}
            </select>
          </Field>
        </>
      )}

      <Field label="Display name (optional)">
        <input
          type="text"
          value={form.name}
          onChange={e => updateField('name', e.target.value)}
          placeholder="Defaults from block, street, or amenity name"
          className={INPUT_CLASS}
          disabled={isSaving}
        />
      </Field>

      <Field label="Landmark (optional)">
        <input
          type="text"
          value={form.landmark}
          onChange={e => updateField('landmark', e.target.value)}
          placeholder="e.g. Near lift 2"
          className={INPUT_CLASS}
          disabled={isSaving}
        />
      </Field>

      <Field
        label="GPS on QR (optional)"
        hint="Stored on the QR record; used when printing or auditing placement."
      >
        <div className="rounded-xl border border-zinc-300 bg-white px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-green-50">
              <MapPin className="h-5 w-5 text-green-700" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-black font-['Open_Sans']">
                {form.location?.address?.trim()
                  ? form.location.address
                  : form.location?.lat != null && form.location?.lng != null
                    ? `${Number(form.location.lat).toFixed(6)}, ${Number(form.location.lng).toFixed(6)}`
                    : 'Pick a location on the map'}
              </p>
              <p className="mt-1 text-xs text-gray-500 font-['Open_Sans']">
                {form.location?.lat != null && form.location?.lng != null
                  ? `Coordinates: ${Number(form.location.lat).toFixed(6)}, ${Number(form.location.lng).toFixed(6)}`
                  : 'Opens a full-screen map to select GPS.'}
              </p>
            </div>

            <div className="flex items-center gap-2">
              {form.location?.lat != null && form.location?.lng != null && !isSaving && (
                <button
                  type="button"
                  onClick={() => updateField('location', null)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Clear location"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowLocationPicker(true)}
                disabled={isSaving}
                className="px-4 py-2 rounded-lg bg-green-600 text-sm font-semibold text-white font-['Open_Sans'] hover:bg-green-700 disabled:opacity-50"
              >
                {form.location?.lat != null && form.location?.lng != null ? 'Change' : 'Pick on map'}
              </button>
            </div>
          </div>
        </div>
      </Field>

      <HaltPicker
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        initialLocation={
          form.location?.lat != null && form.location?.lng != null
            ? { lat: Number(form.location.lat), lng: Number(form.location.lng) }
            : null
        }
        initialAddress={form.location?.address || null}
        onConfirm={haltResult => {
          const lat = haltResult?.location?.lat;
          const lng = haltResult?.location?.lng;
          const address = haltResult?.address || '';
          if (lat == null || lng == null) {
            setShowLocationPicker(false);
            return;
          }
          updateField('location', { address, lat, lng });
          setShowLocationPicker(false);
        }}
      />

      <div className="mt-auto flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 pt-4 border-t border-white/25">
        <button
          type="button"
          onClick={() => navigate('/qr-management')}
          disabled={isSaving}
          className="px-6 py-2.5 rounded-lg border border-white/40 bg-white/15 backdrop-blur-md text-sm font-semibold text-gray-800 font-['Open_Sans'] hover:bg-white/25 disabled:opacity-50 sm:min-w-[120px]"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSaving || sitesLoading}
          className="px-8 py-2.5 rounded-lg bg-green-600 text-sm font-semibold text-white font-['Open_Sans'] hover:bg-green-700 disabled:opacity-50 sm:min-w-[180px]"
        >
          {isSaving ? 'Generating…' : 'Generate QR'}
        </button>
      </div>
    </form>
  );

  return (
    <div className="w-full flex flex-col gap-6 max-w-6xl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h3 className={typography.h3} style={{ color: '#000000' }}>
          Create QR code
        </h3>
        {/* <button
          type="button"
          onClick={() => navigate('/qr-management')}
          className="text-sm text-[#6BABA0] font-['Open_Sans'] hover:underline text-left"
        >
          Back to all QR codes
        </button> */}
      </div>

      {sitesLoading && activeTab === 'single' ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-[#6BABA0] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-2">
            {TABS.map(tab => (
              <button
                key={tab.id}
                type="button"
                className={tabButtonClass(tab.id)}
                onClick={() => {
                  setActiveTab(tab.id);
                  setError('');
                  if (tab.id === 'multiple') {
                    setBulkSiteId(form.site_id || sites[0]?.site_id || '');
                  }
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className={QR_CREATE_PANEL_CLASS}>
            {activeTab === 'single' ? (
              renderSingleForm()
            ) : sitesLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-4 border-[#6BABA0] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <CreateMultipleQR
                sites={sites}
                siteId={bulkSiteId}
                siteAreas={siteAreas}
                areasLoading={areasLoading}
                onSiteChange={setBulkSiteId}
                onGenerate={handleMultipleGenerate}
                onCancel={() => navigate('/qr-management')}
                isSaving={isSaving}
                error={error}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CreateQR;
